import { win } from '@wix/cashier-common/dist/src/utils/win';
import getCountryISO2 from 'country-iso-3-to-2';
import { waitForCondition } from '@wix/cashier-common/dist/src/utils/waitForCondition';
import { type BillingInfo } from '../../types/BillingInfo';
import { type Expiration } from '../../types/Expiration';
import { type CreatePaymentMethodResponse } from '../../types/stripe/createPaymentMethod';
import { TokenizationErrorCodes } from '../../types/TokenizationErrorCodes';
import { filterDefined } from '../../utils/filterDefined';
import { mapStripeTokenizeErrorToErrorCode } from './utils';

interface TokenizeParams {
  publishableKey: string;
  billingInfo?: BillingInfo;
  card: {
    number: string;
    cvv: string;
    expire: Expiration;
  };
}

/**
 * Stripe API
 * Docs: https://stripe.com/docs/api/payment_methods/create#create_payment_method-card
 * Test Cards: https://stripe.com/docs/testing#cards
 */

export const tokenizeViaStripe = (params: TokenizeParams): Promise<string> =>
  new Promise<string>(async (resolve, reject) => {
    const _reject = (message: string, errorCode = TokenizationErrorCodes.GeneralTokenizationError) =>
      reject({ errorCode, message });

    try {
      if (!win.Stripe) {
        try {
          await waitForCondition(() => !!win.Stripe, {
            errorMessage: `Wait for Stripe SDK to load timeout`,
            timeout: 5000,
          });
        } catch {
          console.error('Stripe script is not loaded!');
          return _reject('Missing Stripe object to perform tokenization');
        }
      }

      const { publishableKey, card, billingInfo } = params;

      if (!publishableKey) {
        console.error('Did you provide publishableKey?');
        return _reject('Missed fields', TokenizationErrorCodes.GeneralValidationError);
      } else if (!card.number) {
        console.error('Did you provide ccNo?');
        return _reject('Missed fields', TokenizationErrorCodes.InvalidCardNumber);
      } else if (!card.cvv) {
        console.error('Did you provide cvv?');
        return _reject('Missed fields', TokenizationErrorCodes.InvalidCVV);
      } else if (!card.expire.month || !card.expire.year) {
        console.error('Did you provide expMonth/expYear?');
        return _reject('Missed fields', TokenizationErrorCodes.InvalidExpirationDate);
      }

      const stripe = await win.Stripe(publishableKey);

      const response: CreatePaymentMethodResponse = await stripe.createPaymentMethod('card', {
        card: {
          number: params.card.number,
          cvc: params.card.cvv,
          exp_month: params.card.expire.month,
          exp_year: params.card.expire.year,
        },
        ...(billingInfo && {
          billing_details: filterDefined({
            address: filterDefined({
              country: getCountryISO2(billingInfo.countryCode),
              state: billingInfo.state,
              city: billingInfo.city,
              line1: billingInfo.address,
              ...((billingInfo?.addressDetails?.houseNumber || billingInfo?.addressDetails?.street) && {
                line2: `${billingInfo?.addressDetails?.houseNumber || ''} ${billingInfo?.addressDetails?.street || ''}`,
              }),
              postal_code: billingInfo.zipCode,
            }),
            email: billingInfo.email,
            ...((billingInfo.firstName || billingInfo.lastName) && {
              name: `${billingInfo.firstName || ''} ${billingInfo.lastName || ''}`,
            }),
            phone: billingInfo.phone,
          }),
        }),
      });

      if (response.error) {
        console.error(`tokenize via StripeGateway failed`, response.error);
        return _reject(
          `tokenize via StripeGateway failed. ${JSON.stringify(response.error.message)}`,
          mapStripeTokenizeErrorToErrorCode(response.error),
        );
      }

      resolve(response.paymentMethod.id);
    } catch (error) {
      console.error(`tokenize via StripeGateway unexpectedly failed`, error);
      _reject(`tokenize via StripeGateway unexpectedly failed ${JSON.stringify(error)}`);
    }
  });
